import { Box, Grid, TextField, Typography, Select, MenuItem, Autocomplete } from "@mui/material";
import React, { useState, useEffect } from "react";
import { ITheme } from "../../../utils/theme";
import { EmailField } from "../../../components/FormComponents/EmailField";
import { IEmployeeInfo, IContactEmailData, IContactAddressData, IEmployeeProfileData, IAddressDetailsData, IAddressPredictionsData, IAddressPrediction } from "../ContactAddressData";
import { FormattedPhoneInput } from "../../../components/FormComponents/FormattedPhoneInput";
import { asteriskStyle, addressFieldStyle } from "../styled";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { useFetcher } from "react-router-dom";

interface IUpdateAddressProps {
  onChangeField: (field: keyof IContactAddressData, value: string) => void;
  data: IContactEmailData;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setAddressFormDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  isAddressFormTouched: boolean;
  isAddressFormDisabled: boolean;
  validatedAddress: boolean;
  isAddressValid: () => boolean;
  setLastValidatedAddress: React.Dispatch<React.SetStateAction<{
    addressLine1: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  } | null>>;
  setValidatedAddress: React.Dispatch<React.SetStateAction<boolean>>;
  predictionsFetcher: ReturnType<typeof useFetcher<IAddressPredictionsData>>;
  detailsFetcher: ReturnType<typeof useFetcher<IAddressDetailsData>>;
}

export const UpdateAddress: React.FC<IUpdateAddressProps> = ({
  onChangeField,
  data,
  error,
  setError,
  setAddressFormDisabled,
  isAddressFormTouched,
  isAddressFormDisabled,
  validatedAddress,
  setValidatedAddress,
  isAddressValid,
  setLastValidatedAddress,
  predictionsFetcher,
  detailsFetcher,
}) => {
  const [addressOptions, setAddressOptions] = useState<IAddressPrediction[]>([]);
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const loading = predictionsFetcher.state === 'loading' || detailsFetcher.state === 'loading';

  const { employeeAddress } = data;

  const hadStoredAddress = data && data?.employeeAddress.addressLine1;

  const [hasClearedFields, setHasClearedFields] = useState(false);

  useEffect(() => {
    const predictionsData = predictionsFetcher.data;

    if (!predictionsData) {
      return;
    }

    if (predictionsData.success === false) {
      setError(true);
      setAddressFormDisabled(true);
      setAddressOptions([]);
      return;
    }

    if (predictionsData.predictions) {
      setAddressOptions(predictionsData.predictions || []);
    }
  }, [ predictionsFetcher.data, setError ]);

  useEffect(() => {
    const detailsData = detailsFetcher.data;

    if (!detailsData) {
      return;
    }

    if (detailsData.success === false) {
      setError(true);
      setAddressFormDisabled(true);
      setAddressOptions([]);
      return;
    }

    if (detailsData.details) {
      const response = detailsData.details;
      onChangeField("addressLine1", response.addressLine1);
      onChangeField("addressLine2", response.addressLine2 || "");
      onChangeField("city", response.city);
      onChangeField("province", response.province);
      onChangeField("postalCode", response.postalCode);
      onChangeField("country", response.country);

      setValidatedAddress(true);
      setLastValidatedAddress({
        addressLine1: response.addressLine1,
        city: response.city,
        province: response.province,
        postalCode: response.postalCode,
        country: response.country,
      });
    }
  }, [ detailsFetcher.data, setError, setValidatedAddress, setLastValidatedAddress ]);

  const handleAddressInitialClick = () => {
    if (hadStoredAddress && !hasClearedFields) {
      onChangeField("addressLine1", "");
      onChangeField("addressLine2", "");
      onChangeField("city", "");
      onChangeField("province", "BC");
      onChangeField("postalCode", "");
      onChangeField("country", "Canada");

      setValidatedAddress(false);
      setHasClearedFields(true);
    }
  };

  const handleAddressInputChange = async (inputValue: string, lastId: string | null = null) => {
    if (!isAddressFormTouched) {
      return;
    }
    onChangeField("addressLine1", inputValue);

    if (!inputValue) {
      setAddressOptions([]);
      return;
    }

    let url = `/contact-address/predictions?addressQuery=${encodeURIComponent(inputValue)}`;
    if (lastId) {
      url += `&lastId=${lastId}`;
    }
    predictionsFetcher.load(url);
  };
  
  const handleAddressSelection = async (selectedOption: IAddressPrediction | null) => {

    if (!selectedOption) {
      setDropdownOpen(false);
      return;
    }

    if (selectedOption.next === 'Find') {
      handleAddressInputChange(selectedOption.text, selectedOption.id);
      return;
    }

    if (selectedOption.next === 'Retrieve') {
      setDropdownOpen(false);
      detailsFetcher.load(`/contact-address/details?addressId=${selectedOption.id}`);
    }
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeField("city", e.target.value);
  };

  const handlePostalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeField("postalCode", e.target.value);
  };

  const handleProvinceChange = (
    event: React.SyntheticEvent,
    value: string
  ) => {
    onChangeField("province", value);
  };

  return (
  <Box sx={{ p: { xs: 0, md: 2 } }}>
    {error && <Box sx={{mt: 1, ml: 0.5, mr: 1, color: (theme: ITheme) => theme.palette.error.light, display: "flex"}}>
        <Typography 
          component="span"
          sx={{ml: 1, fontSize: "14px"}}
        >
        The entered address cannot be found. Please call Dispatch Admin at 604-251-6141.
        </Typography>
    </Box>}
    <Grid container spacing={2} sx={{ justifyContent: "center" }}>
      <Grid
        item
        xs={12}
        md={data?.employeeEmail ? 10 : 8}
        sx={{ p: 1 }}
      >
        {<Box sx={{ mt: 2, pl: 2, pr: 2 }}>
          {<Box sx={{mt: 2}}>
            {<Typography sx={{mt: 4}} variant="form-label" data-automation="address1-lbl">
                        Address Line 1
            </Typography>}
            {<AddressAutocomplete
                options={addressOptions}
                loading={loading}
                value={employeeAddress.addressLine1}
                open={isDropdownOpen}
                onOpen={() => setDropdownOpen(true)}
                onClose={() => setDropdownOpen(false)}
                onInputChange={(newValue, reason) => {
                  if (reason === 'input') {
                    handleAddressInputChange(newValue);
                  }
                }}
                onChange={handleAddressSelection}
                onFocus={handleAddressInitialClick}
                disabled={isAddressFormDisabled}
              />}
            </Box>}
          {<Box sx={{mt: 2}}>
            {<Typography sx={{mt: 4}} variant="form-label" data-automation="address2-lbl">
                        Address Line 2
            </Typography>}
            {<TextField
                        fullWidth
                        value={employeeAddress.addressLine2 || ""}
                        onChange={e => onChangeField("addressLine2", e.target.value)}
                        onFocus={handleAddressInitialClick}
                        inputProps={{
                          maxLength: 30,
                        }}
                        data-automation="address2-val"
                        sx={{addressFieldStyle}}
                        disabled={isAddressFormDisabled}
                    />}
            </Box>}
            {<Box sx={{mt: 2}}>
              {<Typography sx={{mt: 4}} variant="form-label" data-automation="city-lbl">
                          City
              </Typography>}
              {<TextField
                          fullWidth
                          value={employeeAddress.city}
                          onInput={handleCityChange}
                          onFocus={handleAddressInitialClick}
                          inputProps={{
                            maxLength: 30,
                          }}
                          data-automation="city-val"
                          sx={{addressFieldStyle}}
                          disabled={isAddressFormDisabled}
                      />}
            </Box>}
            {<Box sx={{mt: 2}}>
              {<Typography sx={{mt: 4}} variant="form-label" data-automation="prov-lbl">
                          Province
              </Typography>}
              {<Autocomplete
                options={data.provinceOptions || []}
                value={employeeAddress.province}
                onChange={handleProvinceChange}
                onFocus={handleAddressInitialClick}
                disableClearable
                disabled={isAddressFormDisabled}
                renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                  }}
                />
              )}
              />}
            </Box>}
            {<Box sx={{mt: 2}}>
              {<Typography sx={{mt: 4}} variant="form-label" data-automation="postal-lbl">
                          Postal Code
              </Typography>}
              {<TextField
                          fullWidth
                          value={employeeAddress.postalCode}
                          onInput={handlePostalChange}
                          onFocus={handleAddressInitialClick}
                          disabled={isAddressFormDisabled}
                          inputProps={{
                            maxLength: 10,
                            autoCapitalize: "none",
                          }}
                          data-automation="postal-val"
                          sx={{ addressFieldStyle }}
                      />}
            </Box>}
            {<Box sx={{mt: 2}}>
              {<Typography sx={{mt: 4}} variant="form-label" data-automation="country-lbl">
                          Country
              </Typography>}
              {<Select
                    fullWidth
                    displayEmpty
                    readOnly
                    value={employeeAddress.country}
                    disabled={isAddressFormDisabled}
                    sx={{
                      '& .MuiSelect-icon': {
                        display: 'none',
                      },
                    }}
                  >
                    {<MenuItem value="Canada">
                      <Typography>
                        Canada
                      </Typography>
                    </MenuItem>}
                  </Select>}
            </Box>}
          </Box>}
      </Grid>
    </Grid>
  </Box>
  );
};
